import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { AccountRouteNames } from '../../shared/routing.constants';
import { AuthService } from './auth.service';

export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private apiUrl: string,
    private router: Router
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.apiUrl) return next.handle(request);

    if (this.apiUrlRequiresWithCredentials(request)) {
      request = this.processRequestWithCredentials(request);
    }

    if (this.isApiOriginUrl(request)) {
      return this.authService.getAccessToken().pipe(
        take(1),
        mergeMap(token => this.processRequestWithToken(token, request, next))
      );
    }

    return next.handle(request);
  }

  // sends request with credentials (cookies)
  private processRequestWithCredentials(
    req: HttpRequest<unknown>
  ): HttpRequest<unknown> {
    req = req.clone({
      withCredentials: true,
    });

    return req;
  }

  // sends request with access token (bearer token in authorization header)
  private processRequestWithToken(
    token: string,
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (token) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return next.handle(req).pipe(
      map((event: HttpEvent<unknown>) => {
        return event;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse.status === HttpStatusCode.Unauthorized) {
          // unauthorized, sign out and redirect to login page

          console.error(
            'auth-Interceptor: Unauthorized, signing out and redirecting to login page',
            errorResponse.url
          );

          this.authService.signOut();
          this.router.navigate(
            [AccountRouteNames.ModuleBase, AccountRouteNames.Login],
            {
              queryParams: {
                returnUrl: this.router.url,
              },
            }
          );
        }

        if (errorResponse.status === HttpStatusCode.BadRequest) {
          // bad request most likely validation error, return only error object
          return throwError(() => errorResponse.error);
        }

        if (errorResponse.status === HttpStatusCode.Forbidden) {
          // forbidden user is authenticated but tried to perform an unauthorized action, return only error object
          return throwError(() => errorResponse.error);
        }

        // everything else, return complete error response
        return throwError(() => errorResponse);
      })
    );
  }

  private apiUrlRequiresWithCredentials(req: HttpRequest<unknown>): boolean {
    if (
      req.url.startsWith(`${this.apiUrl}/User/Impersonate`) ||
      req.url.startsWith(`${this.apiUrl}/User/Login`) ||
      req.url.startsWith(`${this.apiUrl}/User/Token`)
    ) {
      return true;
    }

    return false;
  }

  private isApiOriginUrl(req: HttpRequest<unknown>): boolean {
    if (
      req.url.startsWith(`${this.apiUrl}`) &&
      (!req.url.startsWith(`${this.apiUrl}/User/AzureAdLogin`) ||
        !req.url.startsWith(`${this.apiUrl}/User/Login`) ||
        !req.url.startsWith(`${this.apiUrl}/User/Token`))
    ) {
      return true;
    }

    return false;
  }
}
